import { RoomData, TransformedMarkersData } from '@/models/markers-data.model';
import { LoadingData, ViewerRoom } from '@/models/viewer.model';

Image.prototype.loadImage = function loadImage(
    url: string,
    viewerService: ViewerService,
    room: ViewerRoom,
    defaultRoom: boolean,
) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const thisImg = this;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'arraybuffer';

    xhr.onload = function onload() {
        if (defaultRoom) {
            viewerService.setDefaultRoomLoadingFinished();
        }

        viewerService.loadingData.imagesLoadedCount += 1;
        viewerService.loadingData.loadedImages.push(room.name);

        if (
            viewerService.loadingData.imagesToLoadCount
            === viewerService.loadingData.imagesLoadedCount
        ) {
            viewerService.setOtherRoomsLoadingFinished();
        }

        const blob = new Blob([this.response]);
        thisImg.src = window.URL.createObjectURL(blob);
    };

    if (defaultRoom) {
        xhr.onprogress = function onprogress(e: ProgressEvent) {
            viewerService.setDefaultRoomLoadingProgress(Math.ceil((e.loaded / e.total) * 100));
        };
        xhr.onloadstart = function onloadstart() {
            viewerService.setDefaultRoomLoadingProgress(0);
        };
        xhr.onerror = function onerror() {
            viewerService.setLoadingViewError(true);
        };
    }
    xhr.send();
};

class ViewerService {
    config: TransformedMarkersData;

    rooms: Array<ViewerRoom> = [];

    loadingData: LoadingData = {
        defaultRoom: {
            started: false,
            finished: false,
            loadingProgress: 0,
        },
        otherRooms: {
            started: false,
            finished: false,
        },
        imagesToLoadCount: 0,
        imagesLoadedCount: 0,
        loadedImages: [],
    };

    error: boolean;

    constructor(viewerConfig: TransformedMarkersData) {
        this.error = false;
        this.config = viewerConfig;
        this.initRooms();
    }

    public setActiveRoom(roomName: string) {
        this.resetActiveRoomRotation();
        this.rooms.forEach((room) => {
            room.active = room.name === roomName;
        });
    }

    private initRooms() {
        this.config.rooms.forEach((room) => {
            const roomData = this.createRoom(room);
            this.rooms.push(roomData);
            this.loadingData.imagesToLoadCount += 1;
        });

        this.loadDefaultRoom();
    }

    private createRoom(roomData: RoomData): ViewerRoom {
        return {
            label: roomData.label,
            name: roomData.name,
            image: new Image(),
            texture: null,
            imgPath: roomData.imgPath,
            markers: roomData.markers,
            default: roomData.name === this.getDefaultRoomName(),
            loaded: false,
            active: roomData.name === this.getDefaultRoomName(),
            defaultRotation: roomData.rotation,
            currentRotation: roomData.rotation,
            floor: roomData.floor,
        };
    }

    private loadDefaultRoom() {
        const defaultRoom = this.rooms.find((room) => room.default);

        if (defaultRoom) {
            this.setDefaultRoomLoadingStarted();
            this.loadRoomImage(defaultRoom, true);
        } else {
            console.error('No default room found');
        }
    }

    private loadRoomImage(room: ViewerRoom, defaultRoom = false) {
        // @ts-ignore
        room.image.loadImage(room.imgPath, this, room, defaultRoom);
    }

    public loadMissingRooms() {
        if (!this.loadingData.otherRooms.finished) {
            this.setOtherRoomsLoadingStarted();
            this.rooms.forEach((room) => {
                if (room.default) return;
                this.loadRoomImage(room);
            });
        }
    }

    private setDefaultRoomLoadingStarted() {
        this.loadingData.defaultRoom.started = true;
    }

    public setDefaultRoomLoadingProgress(val: number) {
        this.loadingData.defaultRoom.loadingProgress = val;
    }

    public setDefaultRoomLoadingFinished() {
        this.loadingData.defaultRoom.finished = true;
    }

    private setOtherRoomsLoadingStarted() {
        this.loadingData.otherRooms.started = true;
    }

    public setOtherRoomsLoadingFinished() {
        this.loadingData.otherRooms.finished = true;
    }

    private resetActiveRoomRotation() {
        const activeRoom = this.getActiveRoom();
        if (activeRoom) {
            activeRoom.currentRotation = activeRoom.defaultRotation;
        }
    }

    public setLoadingViewError(state: boolean) {
        this.error = state;
    }

    public getDefaultRoomLoadingProgress() {
        return this.loadingData.defaultRoom.loadingProgress;
    }

    public getDefaultRoomName() {
        return this.config.defaultRoomName;
    }

    public getActiveRoomPhoto() {
        const activeRoom = this.rooms.find((room) => room.active);
        return activeRoom?.image.src;
    }

    public getActiveRoomMarkers() {
        const activeRoom = this.rooms.find((room) => room.active);
        return activeRoom?.markers || [];
    }

    public getActiveRoom() {
        return this.rooms.find((room) => room.active);
    }

    public setRoomTexture(roomName: string, texture: Record<string, unknown>) {
        const activeRoomIndex = this.rooms.findIndex((room) => room.name === roomName);
        if (activeRoomIndex >= 0) {
            this.rooms[activeRoomIndex].texture = texture;
        }
    }
}

export default ViewerService;
